import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
//import ActivityDetailsLayout from '../components/activity-details/ActivityDetailsLayout';
import LibraryActivityCard from '../components/activity-library/LibraryActivityCard';
import LibrarySectionHeader from '../components/activity-library/LibrarySectionHeader';
import EmptyLibraryState from '../components/activity-library/EmptyLibraryState';
import { LibrarySkeletonList } from '../components/activity-library/LibraryActivitySkeleton';
import { useInfiniteBookmarks } from '../hooks/useBookmarks';
import { useUndoableDelete } from '../hooks/useUndoableDelete';
import { useTimeOfDay } from '../components/home-v2';
import ActivityLibraryLayout from '../components/activity-library/ActivityLibraryLayout';
import { CheckInTransitionOverlay, useCheckInTransition } from '../components/home-v2/check-in-transition';
import { useContentManager } from './HomeV2Page';

/**
 * Activity Library Page
 * Displays user's bookmarked activities with infinite scroll
 */
export default function ActivityLibraryPage() {
  const navigate = useNavigate();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteBookmarks(20);

  const { config } = useTimeOfDay();

  const {
    //cloudRef,
    isTransitioning,
    cloudRect,
    startTransition,
    onTransitionComplete,
    // resetTransition,
  } = useCheckInTransition();

  // Track which bookmarks are being deleted
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  // Undo delete functionality
  const { deleteWithUndo, cancelAllPending } = useUndoableDelete({
    onDeleteSuccess: () => {
      // Deletion successful - query will auto-refetch
    },
    onRestoreSuccess: () => {
      // Restoration successful - query will auto-refetch
    },
    undoTimeoutMs: 5000, // 5 seconds to undo
  });

  // Cleanup pending deletions on unmount
  useEffect(() => {
    return () => {
      cancelAllPending();
    };
  }, [cancelAllPending]);

  const handleDelete = async (bookmarkId: number) => {
    const allBookmarks = data?.pages.flatMap((page) => page.bookmarks) ?? [];
    const bookmark = allBookmarks.find((b) => b.id === bookmarkId);
    if (!bookmark) return;

    // Mark as deleting for UI feedback
    setDeletingIds((prev) => new Set(prev).add(bookmarkId));

    try {
      await deleteWithUndo(bookmark);
    } finally {
      // Remove from deleting set after a brief delay for UX
      setTimeout(() => {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(bookmarkId);
          return next;
        });
      }, 300);
    }
  };

  // Flatten pages into single array of bookmarks
  const allBookmarks = data?.pages.flatMap((page) => page.bookmarks) ?? [];
  const totalCount = data?.pages[0]?.pagination.totalCount ?? 0;

  // Navigation handler for DONE button
  const handleDoneClick = () => {
    navigate(-1); // Go back to previous page
  };

  const {
    hideHeaderAvatar,
  } = useContentManager()

  // Scroll to top on mount
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <ActivityLibraryLayout
        timeOfDayConfig={config}
        showAvatar={!hideHeaderAvatar}
        onCloudClick={startTransition}
      >
        {/* Section Header with DONE button */}
        <div style={{
          backgroundColor: config.headerBackground,
        }}>
          <div
            className="bg-white rounded-tl-[24px] rounded-tr-[24px] pt-6 px-6 sm:px-8 md:px-10 lg:px-12 sm:py-5 md:py-6"
          >
            <LibrarySectionHeader onDoneClick={handleDoneClick} />
          </div>
        </div>
        <div
          ref={scrollContainerRef}
          className="bg-white pt-0 px-6 sm:px-8 md:px-10 lg:px-12 pb-6 py-4 min-h-screen"
        >


            {/* Description - Responsive typography */}
            <p className="text-[16px] sm:text-[17px] md:text-[18px] text-[#1B2831] mb-6 sm:mb-7 md:mb-8">
              {!isNaN(totalCount) && totalCount > 0 ? (
                <>Your saved sparks</>
              ) : null
              }
            </p>

            {/* Loading State */}
            {isLoading && (
              <div>
                <LibrarySkeletonList count={5} />
              </div>
            )}

            {/* Error State */}
            {isError && (
              <div className="text-center py-16">
                <p className="text-red-600 mb-4">
                  {error instanceof Error ? error.message : 'Failed to load activities'}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-6 py-2 bg-[#FF6B35] text-white rounded-lg hover:bg-[#FF5722]"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !isError && allBookmarks.length === 0 && (
              <EmptyLibraryState />
            )}

            {/* Bookmarked Activities with Infinite Scroll */}
            {!isLoading && !isError && allBookmarks.length > 0 && (
              <InfiniteScroll
                dataLength={allBookmarks.length}
                next={fetchNextPage}
                hasMore={hasNextPage ?? false}
                loader={
                  <div className="py-4">
                    <LibrarySkeletonList count={2} />
                  </div>
                }
                endMessage={
                  <p className="text-center py-8 text-gray-500">
                    You&apos;ve reached the end of your library
                  </p>
                }
                scrollableTarget="window"
                className="space-y-0"
              >
                {allBookmarks.map((bookmark) => (
                  <LibraryActivityCard
                    key={bookmark.id}
                    bookmark={bookmark}
                    onDelete={handleDelete}
                    isDeleting={deletingIds.has(bookmark.id)}
                  />
                ))}
              </InfiniteScroll>
            )}

            {/* Loading More Indicator (outside InfiniteScroll for visibility) */}
            {isFetchingNextPage && !isLoading && (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-[#FF6B35]" />
              </div>
            )}
          </div>
      </ActivityLibraryLayout>

      {/* Check-in transition overlay */}
      {isTransitioning && (
        <CheckInTransitionOverlay
          cloudRect={cloudRect}
          onComplete={onTransitionComplete}
        // onReplay={resetTransition}
        />
      )}

    </>
  );
}
